import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Modal,
  Grid,
  Select,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  Backdrop,
  Fade,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

function getCSRFToken() {
  const name = "csrftoken";
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

const VARIANT_TYPES = ["Color", "Capacidad", "Tamaño", "Presentación", "Material", "Medida"];
const IVA_OPTIONS = [0, 5, 15, 999];

const headCellSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const actionButtonSx = {
  width: 44,
  height: 38,
  borderRadius: 1.6,
  border: "1px solid #dbeafe",
  bgcolor: "#f8fbff",
  color: "#1565C0",
  "&:hover": {
    bgcolor: "#eef6ff",
  },
};

const deleteButtonSx = {
  width: 44,
  height: 38,
  borderRadius: 1.6,
  border: "1px solid #fecaca",
  bgcolor: "#fff7f7",
  color: "#d32f2f",
  "&:hover": {
    bgcolor: "#ffefef",
  },
};

const modalBoxSx = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "92%", sm: "84%", md: "78%", lg: "72%" },
  maxHeight: "90vh",
  bgcolor: "#fff",
  borderRadius: 3,
  boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
  overflow: "hidden",
};

export default function AdminVariant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openListModal, setOpenListModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [currentProductEntry, setCurrentProductEntry] = useState(null);
  const [currentVariant, setCurrentVariant] = useState(null);
  const [variantsInModal, setVariantsInModal] = useState([]);
  const [allowedVariantType, setAllowedVariantType] = useState("");
  const [fields, setFields] = useState({
    variant_type: "",
    value: "",
    price: "",
    stock_minimo: "",
    sku: "",
    image: null,
    iva_porcentaje: 15,
    iva_valor: "",
  });
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const fmt = (n) => {
    const v = Number(n);
    return Number.isFinite(v) ? v.toFixed(2) : "0.00";
  };

  const calcIva = (price, ivaPorcentaje) => {
    const p = Number(price);
    const iva = Number(ivaPorcentaje);
    if (!Number.isFinite(p)) return "0.00";
    if (iva === 999) return "0.00";
    return ((p * iva) / 100).toFixed(2);
  };

  const loadProductsWithVariants = async () => {
    setLoading(true);
    try {
      const res = await fetch("/productos/ver-productos-con-variantes/", {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("No se pudieron cargar los productos con variantes");
      }

      const { products = [] } = await res.json();

      setEntries(
        products.map((p) => ({
          product: p,
          variants: [],
        }))
      );
    } catch (e) {
      setErrorMsg(e.message || "No se pudieron cargar variantes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProductsWithVariants();
  }, []);

  useEffect(() => {
    setFields((prev) => ({
      ...prev,
      iva_valor: calcIva(prev.price, prev.iva_porcentaje),
    }));
  }, [fields.price, fields.iva_porcentaje]);

  const filtered = entries.filter(
    ({ product }) =>
      (product.name || "").toLowerCase().includes((search || "").toLowerCase()) ||
      (product.code || "").toLowerCase().includes((search || "").toLowerCase())
  );

  const openVariants = async (entry) => {
    try {
      const res = await fetch(`/productos/ver-variantes/?code=${encodeURIComponent(entry.product.code)}`, {
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error("No se pudieron cargar las variantes");
      }
      const data = await res.json();
      const variants = Array.isArray(data.variants) ? data.variants : [];
      setCurrentProductEntry({ ...entry, variants });
      setVariantsInModal(variants);
      const firstType = variants.length ? variants[0].variant_type : "";
      setAllowedVariantType(firstType);
      setOpenListModal(true);
    } catch (e) {
      setErrorMsg(e.message || "No se pudieron cargar las variantes");
    }
  };

  const openEdit = (variant) => {
    setCurrentVariant(variant);
    setFields({
      variant_type: variant.variant_type || "",
      value: variant.value || "",
      price: String(variant.price ?? ""),
      stock_minimo: variant.stock_minimo ?? "",
      sku: variant.sku || "",
      image: variant.image || null,
      iva_porcentaje: variant.iva_porcentaje ?? 15,
      iva_valor: variant.iva_valor ?? "",
    });
    setOpenEditModal(true);
  };

  const handleField = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFields((f) => ({ ...f, image: files && files[0] ? files[0] : null }));
    } else {
      setFields((f) => ({ ...f, [name]: value }));
    }
  };

  const saveEdit = async () => {
    try {
      if (allowedVariantType && fields.variant_type && fields.variant_type !== allowedVariantType) {
        setErrorMsg(`Este producto solo permite variantes del tipo "${allowedVariantType}".`);
        return;
      }

      const csrf = getCSRFToken();
      const body = new FormData();
      body.append("variant_type", fields.variant_type);
      body.append("value", fields.value);
      body.append("price", fields.price);
      if (fields.stock_minimo !== "" && fields.stock_minimo !== null && fields.stock_minimo !== undefined) {
        body.append("stock_minimo", String(fields.stock_minimo));
      }
      body.append("sku", fields.sku);
      body.append("iva_porcentaje", String(fields.iva_porcentaje));
      if (fields.image instanceof File) {
        body.append("image", fields.image);
      }

      const res = await fetch(`/productos/actualizar-variante/${currentVariant.id}/`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrf },
        body,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Error al actualizar");
      }

      const result = await res.json();

      const updated = {
        ...currentVariant,
        ...fields,
        price: parseFloat(fields.price || "0"),
        iva_porcentaje: result.iva_porcentaje ?? fields.iva_porcentaje,
        iva_valor: result.iva_valor ?? fields.iva_valor,
        image: result.image || (fields.image instanceof File ? currentVariant.image : fields.image),
        slug: result.slug || currentVariant.slug,
      };

      setVariantsInModal((vs) => vs.map((v) => (v.id === updated.id ? updated : v)));
      setEntries((es) =>
        es.map((en) =>
          en.product.id === currentProductEntry.product.id
            ? { ...en, variants: en.variants.map((v) => (v.id === updated.id ? updated : v)) }
            : en
        )
      );

      setSuccessMsg("Variante actualizada");
      setOpenEditModal(false);
    } catch (e) {
      setErrorMsg(e.message || "Error al actualizar");
    }
  };

  const deleteVariant = async (id) => {
    try {
      const csrf = getCSRFToken();
      const res = await fetch(`/productos/eliminar-variante/${id}/`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRFToken": csrf },
      });

      if (!res.ok) {
        throw new Error("Error al eliminar");
      }

      const newVariants = variantsInModal.filter((v) => v.id !== id);
      setVariantsInModal(newVariants);

      const nextEntries = entries
        .map((en) =>
          en.product.id === currentProductEntry.product.id
            ? { ...en, variants: en.variants.filter((v) => v.id !== id) }
            : en
        )
        .filter((en) => en.variants.length > 0);

      setEntries(nextEntries);

      if (newVariants.length === 0) {
        setOpenListModal(false);
      }

      setSuccessMsg("Variante eliminada");
    } catch (e) {
      setErrorMsg(e.message || "Error al eliminar");
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
              onClick={() => navigate("nueva")}
            >
              Administrar
            </Button>
          </Box>

          <Paper
            sx={{
              p: 2.5,
              borderRadius: 3,
              boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              spacing={2}
              mb={2}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                  Variantes de productos
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                  Gestiona productos con variantes y revisa su stock agrupado
                </Typography>
              </Box>

              <Chip
                label={`${filtered.length} registros`}
                color="info"
                variant="outlined"
                sx={{ fontWeight: 700, borderRadius: 2 }}
              />
            </Stack>

            <TextField
              fullWidth
              placeholder="Buscar producto"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2.2 }}
            />

            <Box
              sx={{
                borderRadius: 2,
                border: "1px solid #e2e8f0",
                overflow: "hidden",
                bgcolor: "#fff",
              }}
            >
              <TableContainer
                sx={{
                  overflowX: "auto",
                  overflowY: "hidden",
                  maxWidth: "100%",
                  "&::-webkit-scrollbar": { height: 10 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#cbd5e1",
                    borderRadius: 10,
                  },
                }}
              >
                <Table size="small" sx={{ minWidth: 880 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headCellSx, minWidth: 160 }}>Código</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 360 }}>Producto</TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 150 }} align="center">
                        Variantes
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 150 }} align="center">
                        Stock total
                      </TableCell>
                      <TableCell sx={{ ...headCellSx, minWidth: 170 }} align="center">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={26} />
                        </TableCell>
                      </TableRow>
                    ) : filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                          <Stack spacing={0.75} alignItems="center">
                            <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                              No hay variantes para mostrar
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                              Ajusta la búsqueda para encontrar resultados
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((en) => {
                          const p = en.product;
                          const variantsCount = Number(p.variant_count || 0);

                          return (
                            <TableRow
                              key={p.id}
                              hover
                              sx={{
                                "& td": { py: 1.2, borderBottom: "1px solid #edf2f7" },
                                "&:hover": { bgcolor: "#fafcff" },
                              }}
                            >
                              <TableCell sx={{ minWidth: 160 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1565C0" }}>
                                  {p.code || "-"}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ minWidth: 360 }}>
                                <Typography
                                  sx={{
                                    fontSize: 13.5,
                                    fontWeight: 800,
                                    color: "#0f172a",
                                    textTransform: "uppercase",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {p.name || "-"}
                                </Typography>
                              </TableCell>

                              <TableCell align="center" sx={{ minWidth: 150 }}>
                                <Chip
                                  label={`${variantsCount} ${variantsCount === 1 ? "variante" : "variantes"}`}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    bgcolor: "#ececec",
                                    "& .MuiChip-label": { px: 1, fontSize: 11.5, color: "#0f172a" },
                                  }}
                                />
                              </TableCell>

                              <TableCell align="center" sx={{ minWidth: 150 }}>
                                <Typography sx={{ fontSize: 13, color: "#0f172a" }}>
                                  -
                                </Typography>
                              </TableCell>

                              <TableCell align="center" sx={{ minWidth: 170 }}>
                                <Stack direction="row" spacing={1} justifyContent="center" useFlexGap flexWrap="wrap">
                                  <Tooltip title="Ver variantes" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => openVariants(en)}
                                      sx={actionButtonSx}
                                    >
                                      <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  borderTop: "1px solid #edf2f7",
                  ".MuiTablePagination-toolbar": {
                    px: { xs: 1, sm: 2 },
                  },
                }}
              />
            </Box>
          </Paper>

          <Modal
            open={openListModal}
            onClose={() => setOpenListModal(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 250 }}
          >
            <Fade in={openListModal}>
              <Box sx={modalBoxSx}>
                <Box
                  sx={{
                    px: 2.2,
                    py: 1.6,
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                      Variantes de {currentProductEntry?.product?.name || "-"}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.35 }}>
                      Código: {currentProductEntry?.product?.code || "-"}
                    </Typography>
                  </Box>

                  <Button
                    onClick={() => setOpenListModal(false)}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Cerrar
                  </Button>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`${variantsInModal.length} ${variantsInModal.length === 1 ? "variante" : "variantes"}`}
                      color="info"
                      variant="outlined"
                      sx={{ fontWeight: 700, borderRadius: 2 }}
                    />
                  </Box>

                  <Box
                    sx={{
                      borderRadius: 2,
                      border: "1px solid #e2e8f0",
                      overflow: "hidden",
                      bgcolor: "#fff",
                    }}
                  >
                    <TableContainer
                      sx={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        maxWidth: "100%",
                        "&::-webkit-scrollbar": { height: 10 },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#cbd5e1",
                          borderRadius: 10,
                        },
                      }}
                    >
                      <Table size="small" sx={{ minWidth: 1180 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ ...headCellSx, minWidth: 130 }}>Imagen</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 150 }}>Tipo</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 160 }}>Valor</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 120 }} align="right">
                              Precio
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 110 }} align="center">
                              IVA
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 130 }} align="center">
                              IVA $
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 140 }} align="center">
                              Stock / Mín
                            </TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 140 }}>SKU</TableCell>
                            <TableCell sx={{ ...headCellSx, minWidth: 170 }} align="center">
                              Acción
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {variantsInModal.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                <Stack spacing={0.75} alignItems="center">
                                  <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                                    No hay variantes para mostrar
                                  </Typography>
                                  <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                                    Este producto no tiene variantes disponibles
                                  </Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ) : (
                            variantsInModal.map((v) => {
                              const low = v.stock_minimo != null && Number(v.stock ?? 0) <= Number(v.stock_minimo);

                              return (
                                <TableRow
                                  key={v.id}
                                  hover
                                  sx={{
                                    "& td": { py: 1.1, borderBottom: "1px solid #edf2f7" },
                                    "&:hover": { bgcolor: "#fafcff" },
                                  }}
                                >
                                  <TableCell sx={{ minWidth: 130 }}>
                                    {v.image ? (
                                      <Box
                                        sx={{
                                          width: 68,
                                          height: 68,
                                          borderRadius: 2,
                                          border: "1px solid #dbeafe",
                                          bgcolor: "#f8fbff",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <Box
                                          component="img"
                                          src={v.image}
                                          alt=""
                                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                                        />
                                      </Box>
                                    ) : (
                                      <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>
                                        -
                                      </Typography>
                                    )}
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 150 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                      {v.variant_type || "-"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 160 }}>
                                    <Typography sx={{ fontSize: 13, color: "#0f172a" }}>
                                      {v.value || "-"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="right" sx={{ minWidth: 120 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                                      {fmt(v.price)}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 110 }}>
                                    <Chip
                                      label={v.iva_porcentaje === 999 ? "No IVA" : `${v.iva_porcentaje}%`}
                                      size="small"
                                      color={v.iva_porcentaje === 999 ? "default" : "info"}
                                      variant={v.iva_porcentaje === 999 ? "outlined" : "filled"}
                                      sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        height: 24,
                                        "& .MuiChip-label": { px: 1, fontSize: 11 },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 130 }}>
                                    <Typography sx={{ fontSize: 13, color: "#0f172a" }}>
                                      {fmt(v.iva_valor)}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 140 }}>
                                    <Chip
                                      label={`${v.stock ?? 0} / ${v.stock_minimo ?? "-"}`}
                                      size="small"
                                      color={low ? "error" : "success"}
                                      variant={low ? "filled" : "outlined"}
                                      sx={{
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        height: 24,
                                        "& .MuiChip-label": { px: 1, fontSize: 11 },
                                      }}
                                    />
                                  </TableCell>

                                  <TableCell sx={{ minWidth: 140 }}>
                                    <Typography sx={{ fontSize: 12.5, color: "#0f172a" }}>
                                      {v.sku || "-"}
                                    </Typography>
                                  </TableCell>

                                  <TableCell align="center" sx={{ minWidth: 170 }}>
                                    <Stack direction="row" spacing={1} justifyContent="center" useFlexGap flexWrap="wrap">
                                      <Tooltip title="Editar" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => openEdit(v)}
                                          sx={actionButtonSx}
                                        >
                                          <EditOutlinedIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                      </Tooltip>

                                      <Tooltip title="Eliminar" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => deleteVariant(v.id)}
                                          sx={deleteButtonSx}
                                        >
                                          <DeleteOutlineRoundedIcon sx={{ fontSize: 20 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Modal>

          <Modal
            open={openEditModal}
            onClose={() => setOpenEditModal(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 250 }}
          >
            <Fade in={openEditModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "92%", sm: 540 },
                  maxHeight: "90vh",
                  bgcolor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2.2,
                    py: 1.6,
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                    Editar variante
                  </Typography>

                  <Button
                    onClick={() => setOpenEditModal(false)}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Cerrar
                  </Button>
                </Box>

                <Box sx={{ p: 2.5, overflowY: "auto", maxHeight: "calc(90vh - 64px)" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo</InputLabel>
                        <Select name="variant_type" label="Tipo" value={fields.variant_type} onChange={handleField}>
                          {VARIANT_TYPES.map((t) => (
                            <MenuItem key={t} value={t} disabled={!!allowedVariantType && allowedVariantType !== t}>
                              {t}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Valor" name="value" value={fields.value} onChange={handleField} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Precio" name="price" type="number" value={fields.price} onChange={handleField} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>IVA</InputLabel>
                        <Select name="iva_porcentaje" label="IVA" value={fields.iva_porcentaje} onChange={handleField}>
                          {IVA_OPTIONS.map((o) => (
                            <MenuItem key={o} value={o}>
                              {o === 999 ? "No objeto de IVA" : `${o}%`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="IVA $" value={fmt(fields.iva_valor)} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Stock mínimo"
                        name="stock_minimo"
                        type="number"
                        value={fields.stock_minimo}
                        onChange={handleField}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField fullWidth label="SKU" name="sku" value={fields.sku} InputProps={{ readOnly: true }} />
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        sx={{
                          width: "100%",
                          height: 150,
                          border: "1px solid #dbeafe",
                          borderRadius: 2,
                          bgcolor: "#f8fbff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        {fields.image ? (
                          <Box
                            component="img"
                            src={typeof fields.image === "string" ? fields.image : URL.createObjectURL(fields.image)}
                            alt="preview"
                            sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                          />
                        ) : (
                          <Typography sx={{ color: "#64748b" }}>Sin imagen</Typography>
                        )}
                      </Box>

                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<AddPhotoAlternateOutlinedIcon />}
                        fullWidth
                        sx={{
                          mt: 1.5,
                          textTransform: "none",
                          borderColor: "#1565C0",
                          color: "#1565C0",
                          fontWeight: 700,
                          "&:hover": { borderColor: "#0D47A1", color: "#0D47A1" },
                        }}
                      >
                        Cambiar imagen
                        <input type="file" hidden accept="image/*" name="image" onChange={handleField} />
                      </Button>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ textAlign: "right" }}>
                    <Button
                      variant="contained"
                      onClick={saveEdit}
                      sx={{
                        textTransform: "none",
                        bgcolor: "#1565C0",
                        "&:hover": { bgcolor: "#0D47A1" },
                        borderRadius: 2,
                        px: 2.2,
                        fontWeight: 700,
                        boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
                      }}
                    >
                      Guardar cambios
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Modal>

          <Snackbar
            open={!!successMsg}
            autoHideDuration={3000}
            onClose={() => setSuccessMsg("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="success" onClose={() => setSuccessMsg("")} sx={{ width: "100%" }}>
              {successMsg}
            </Alert>
          </Snackbar>

          <Snackbar
            open={!!errorMsg}
            autoHideDuration={3000}
            onClose={() => setErrorMsg("")}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ width: "100%" }}>
              {errorMsg}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Box>
  );
}